import {
  experienceForLevel,
  type AbilityId,
  type CombatFeedbackMessage,
  type PlayerView,
} from "@mucki/shared";
import { createAbilityBar } from "./abilityBar.js";
import { createBar } from "./bars.js";
import { createCombatText } from "./combatText.js";
import { createTargetFrame, type TargetInfo } from "./targetFrame.js";

export interface HudView {
  player: PlayerView | undefined;
  target: TargetInfo | undefined;
  now: number;
}

export interface Hud {
  /** Exposed so later units can mount their own indicators into the overlay. */
  element: HTMLElement;
  update(view: HudView): void;
  startCooldown(abilityId: AbilityId, at: number): void;
  spawnCombatText(message: CombatFeedbackMessage, screen: { x: number; y: number }): void;
  dispose(): void;
}

/**
 * DOM elements updated from state, not a UI framework (KTD9). Text is set with
 * `textContent` throughout, which satisfies R29 by construction.
 */
export function createHud(parent: HTMLElement = document.body): Hud {
  const element = document.createElement("div");
  element.className = "hud";

  const panel = document.createElement("div");
  panel.className = "hud-panel";

  const level = document.createElement("div");
  level.className = "hud-level";
  level.textContent = "Level 1";

  const health = createBar("health");
  const energy = createBar("energy");
  const experience = createBar("experience");

  panel.append(level, health.element, energy.element, experience.element);

  const abilityBar = createAbilityBar();
  const targetFrame = createTargetFrame();
  const combatText = createCombatText();

  element.append(panel, abilityBar.element, targetFrame.element);
  parent.appendChild(element);

  return {
    element,
    update: ({ player, target, now }) => {
      if (player) {
        level.textContent = `Level ${player.level}`;
        health.set(player.health, player.maxHealth);
        energy.set(player.energy, player.maxEnergy);
        const needed = experienceForLevel(player.level);
        experience.set(player.experience, needed, `${Math.round(player.experience)} / ${needed} XP`);
        abilityBar.update(now, player.energy);
      }
      targetFrame.update(target, now);
    },
    startCooldown: abilityBar.startCooldown,
    spawnCombatText: combatText.spawn,
    dispose: () => {
      combatText.dispose();
      element.remove();
    },
  };
}
